import { useState } from 'react'
import { Copy, Plus, Workflow } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type InstanceStatus = 'in-progress' | 'blocked' | 'done'

interface ProcessTemplate {
  id: string
  name: string
  steps: number
  instances: number
  lastUpdated: string
  owner: string
}

interface ProcessInstance {
  id: string
  template: string
  customer: string
  currentStep: string
  stepNum: number
  totalSteps: number
  owner: string
  dueIn: string
  status: InstanceStatus
}

interface Staff {
  id: string
  name: string
  initials: string
}

interface Customer {
  id: string
  name: string
  short: string
  color: string
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: InstanceStatus }) {
  const map: Record<InstanceStatus, { label: string; bg: string; color: string }> = {
    'in-progress': { label: 'In progress', bg: 'var(--ns-brand-soft)',  color: 'var(--ns-brand-strong)' },
    'blocked':     { label: 'Blocked',     bg: 'var(--ns-err-soft)',    color: 'var(--ns-err)'          },
    'done':        { label: 'Done',        bg: 'var(--ns-ok-soft)',     color: 'var(--ns-ok)'           },
  }
  const { label, bg, color } = map[status]
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 500,
      background: bg,
      color,
      border: '1px solid transparent',
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }} />
      {label}
    </span>
  )
}

// ─── StepStrip ────────────────────────────────────────────────────────────────

function StepStrip({ stepNum, totalSteps, status }: { stepNum: number; totalSteps: number; status: InstanceStatus }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
      {Array.from({ length: totalSteps }).map((_, idx) => {
        const isPast    = idx < stepNum - 1
        const isCurrent = idx === stepNum - 1
        let bg: string
        if (isPast) {
          bg = 'var(--ns-brand-strong)'
        } else if (isCurrent) {
          bg = status === 'blocked' ? 'var(--ns-err)' : 'var(--ns-brand)'
        } else {
          bg = 'var(--ns-surface-3)'
        }
        return (
          <div
            key={idx}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: bg,
            }}
          />
        )
      })}
    </div>
  )
}

// ─── InstanceCard ─────────────────────────────────────────────────────────────

function InstanceCard({ instance, staff, customers }: { instance: ProcessInstance; staff: Staff[]; customers: Customer[] }) {
  const owner    = staff.find(s => s.id === instance.owner) ?? { initials: instance.owner.slice(0, 2).toUpperCase(), name: instance.owner }
  const customer = customers.find(c => c.id === instance.customer) ?? { name: instance.customer, short: instance.customer.slice(0, 2).toUpperCase(), color: 'var(--ns-text-muted)' }
  const pct      = Math.round((instance.stepNum / instance.totalSteps) * 100)
  const isOverdue = instance.dueIn.startsWith('Overdue')
  const isToday   = instance.dueIn === 'Today'

  let dueColor: string
  if (isOverdue)    dueColor = 'var(--ns-err)'
  else if (isToday) dueColor = 'var(--ns-warn)'
  else              dueColor = 'var(--ns-text)'

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="row" style={{ gap: 10, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{instance.template}</span>
            <span className="mono muted" style={{ fontSize: 11.5 }}>{instance.id}</span>
            <StatusBadge status={instance.status} />
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, height: 22,
            padding: '0 8px 0 4px', borderRadius: 999, background: 'var(--ns-surface-2)',
            border: '1px solid var(--ns-border)', fontSize: 12, fontWeight: 500, color: 'var(--ns-text)',
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: '50%', background: customer.color,
              color: 'white', fontSize: 9, fontWeight: 700, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: '-0.02em',
            }}>{customer.short}</span>
            {customer.name}
          </span>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="col" style={{ alignItems: 'flex-end', gap: 2 }}>
            <span className="muted" style={{ fontSize: 11 }}>DUE</span>
            <span className="mono" style={{ fontSize: 12.5, color: dueColor, fontWeight: 500 }}>
              {instance.dueIn}
            </span>
          </div>
          <div className="avatar sm" title={owner.name}>{owner.initials}</div>
        </div>
      </div>
      <StepStrip stepNum={instance.stepNum} totalSteps={instance.totalSteps} status={instance.status} />
      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
        <span>
          <span className="muted">Step {instance.stepNum}/{instance.totalSteps}: </span>
          <span style={{ fontWeight: 500 }}>{instance.currentStep}</span>
        </span>
        <span className="muted tabular">{pct}% complete</span>
      </div>
    </div>
  )
}

// ─── TemplateCard ─────────────────────────────────────────────────────────────

function TemplateCard({ template, staff }: { template: ProcessTemplate; staff: Staff[] }) {
  const owner = staff.find(s => s.id === template.owner) ?? { initials: template.owner.slice(0, 2).toUpperCase(), name: template.owner }
  return (
    <div className="card" style={{ padding: 16, cursor: 'pointer' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <Workflow size={18} style={{ color: 'var(--ns-brand-strong)' }} />
        <span className="badge">{template.steps} steps</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{template.name}</div>
      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
        <span className="muted">{template.instances} active</span>
        <span className="muted">Updated {template.lastUpdated}</span>
      </div>
      <div className="divider" style={{ margin: '12px 0 8px' }} />
      <div className="row" style={{ gap: 6, fontSize: 12 }}>
        <div className="avatar sm">{owner.initials}</div>
        <span className="muted">{owner.name}</span>
      </div>
    </div>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 10, padding: '64px 0', color: 'var(--ns-text-muted)',
    }}>
      <Workflow size={32} style={{ opacity: 0.3 }} />
      <span style={{ fontSize: 14 }}>{label}</span>
    </div>
  )
}

// ─── ProcessPage ──────────────────────────────────────────────────────────────

export default function ProcessPage() {
  const [tab, setTab] = useState<'instances' | 'templates'>('instances')

  // TODO: replace with API query when backend is implemented
  const templates: ProcessTemplate[] = []
  const instances: ProcessInstance[] = []
  const staff: Staff[]               = []
  const customers: Customer[]        = []

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Process</div>
          <h1 className="page-title">Repeatable workflows</h1>
          <div className="page-subtitle">
            {templates.length} templates · {instances.length} active instances
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn">
            <Copy size={14} />Duplicate template
          </button>
          <button className="btn btn-brand">
            <Plus size={14} />New process
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab${tab === 'instances' ? ' active' : ''}`}
          onClick={() => setTab('instances')}
        >
          Active instances<span className="count">{instances.length}</span>
        </button>
        <button
          className={`tab${tab === 'templates' ? ' active' : ''}`}
          onClick={() => setTab('templates')}
        >
          Templates<span className="count">{templates.length}</span>
        </button>
      </div>

      {tab === 'instances' && (
        instances.length === 0
          ? <EmptyState label="No active process instances" />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {instances.map(instance => (
                <InstanceCard key={instance.id} instance={instance} staff={staff} customers={customers} />
              ))}
            </div>
          )
      )}

      {tab === 'templates' && (
        templates.length === 0
          ? <EmptyState label="No process templates yet" />
          : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}>
              {templates.map(template => (
                <TemplateCard key={template.id} template={template} staff={staff} />
              ))}
            </div>
          )
      )}
    </div>
  )
}
